import React, { useState, useEffect } from 'react';
import Header from '../../../components/Header/Header';
import Footer from '../../../components/Footer/Footer';
import { useNavigate } from 'react-router-dom';
import { Button, TextField, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function UserRole() {
  const [rolesData, setRolesData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editFields, setEditFields] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRolesData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/user/mroles`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setRolesData(data);
        } else {
          console.error('Unexpected data format:', data);
        }
      } catch (error) {
        console.error('Error fetching roles data:', error);
      }
    };

    fetchRolesData();
  }, []);

  const handleEdit = (index) => {
    setEditingRow(index);
    setEditFields(rolesData[index]);
  };

  const handleFieldChange = (field, value) => {
    setEditFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const { RoleID, ...updatedFields } = editFields;

      // Convert Authorization to numeric value
      if ('Authorization' in updatedFields) {
        updatedFields.Authorization = updatedFields.Authorization === 'Authorized' ? 1 : 0;
      }

      const response = await fetch(`${API_BASE_URL}/admin/user/mroles/${RoleID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedFields),
      });

      if (response.ok) {
        const updatedRolesData = [...rolesData];
        updatedRolesData[editingRow] = editFields;
        setRolesData(updatedRolesData);
        setEditingRow(null);
      } else {
        console.error('Error saving data:', await response.text());
      }
    } catch (error) {
      console.error('Error saving data:', error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 text-gray-800">
      <Header />
      <main className="flex-grow px-6 py-8">
        {/* Navigation Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <button
            className="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition"
            onClick={() => navigate('/admin/project_head')}
          >
            Project Head
          </button>
          <button
            className="w-full py-3 bg-purple-500 text-white font-semibold rounded-lg shadow-md hover:bg-purple-600 transition"
            onClick={() => navigate('/admin/location_head')}
          >
            Location Head
          </button>
        </div>

        {/* Roles Table */}
        <div className="overflow-auto shadow-lg rounded-lg">
          <table className="min-w-full bg-white">
            <thead>
              <tr className="bg-gray-200 text-left">
                <th className="py-3 px-4 font-semibold">Role ID</th>
                <th className="py-3 px-4 font-semibold">User ID</th>
                <th className="py-3 px-4 font-semibold">Username</th>
                <th className="py-3 px-4 font-semibold">Role</th>
                <th className="py-3 px-4 font-semibold">Email</th>
                <th className="py-3 px-4 font-semibold">Indent Rights</th>
                <th className="py-3 px-4 font-semibold">Authorization</th>
                <th className="py-3 px-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rolesData.map((role, index) => (
                <tr
                  key={role.RoleID}
                  className={`border-t ${editingRow === index ? 'bg-yellow-100' : 'bg-white'}`}
                >
                  <td className="py-3 px-4">{role.RoleID}</td>
                  <td className="py-3 px-4">{role.UserID}</td>
                  <td className="py-3 px-4">{role.Username}</td>
                  {editingRow === index ? (
                    <>
                      <td className="py-3 px-4">
                        <select
                          value={editFields.Role}
                          onChange={(e) => handleFieldChange('Role', e.target.value)}
                          className="w-full py-2 px-3 border rounded-lg"
                        >
                          <option value="Project Engineer">Project Engineer</option>
                          <option value="Location Head">Location Head</option>
                          <option value="Director">L2 Director</option>
                          <option value="Store Officer">Store Officer</option>
                          <option value="Store Head">Store Head</option>
                          <option value="Purchase Department">Purchase Department</option>
                          <option value="Purchase Head">Purchase Head</option>
                          <option value="Normal">Normal</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <input
                          type="email"
                          value={editFields.Email}
                          onChange={(e) => handleFieldChange('Email', e.target.value)}
                          className="w-full py-2 px-3 border rounded-lg"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={editFields.IndentCreationRights}
                          onChange={(e) => handleFieldChange('IndentCreationRights', e.target.value)}
                          className="w-full py-2 px-3 border rounded-lg"
                        >
                          <option value="Normal">Normal</option>
                          <option value="Special">Special</option>
                          <option value="Emergency">Emergency</option>
                        </select>
                      </td>
                      <td className="py-3 px-4">
                        <select
                          value={editFields.isAuthorized === 1 ? 'Authorized' : 'Not Authorized'}
                          onChange={(e) => handleFieldChange('isAuthorized', e.target.value === 'Authorized' ? 1 : 0)}
                          className="w-full py-2 px-3 border rounded-lg"
                        >
                          <option value="Authorized">Authorized</option>
                          <option value="Not Authorized">Not Authorized</option>
                        </select>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 px-4">{role.Role}</td>
                      <td className="py-3 px-4">{role.Email}</td>
                      <td className="py-3 px-4">{role.IndentCreationRights}</td>
                      <td className={`py-3 px-4 ${role.isAuthorized === 1 ? 'text-green-600' : 'text-red-600'}`}>
                        {role.isAuthorized === 1 ? 'Authorized' : 'Not Authorized'}
                      </td>
                    </>
                  )}
                  <td className="py-3 px-4">
                    {editingRow === index ? (
                      <button
                        onClick={handleSave}
                        className="w-full py-2 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-600 transition"
                      >
                        Save
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEdit(index)}
                        className="w-full py-2 bg-yellow-500 text-white font-semibold rounded-lg shadow-md hover:bg-yellow-600 transition"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default UserRole
